import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import _get from 'lodash.get';

import {
    Select,
    CalculatorStepAccordion,
    FormComputed,
    useForm,
    MessagePropType,
    useCalculator,
} from '@ratehub/base-ui';
import { getPaymentFrequencyAndDivisor, SCENARIO_TYPES } from '@ratehub/mtg-common';

import { HOME_TYPES } from '../definitions/HomeTypes';
import MONTHLY_EXPENSES_DEFAULT_VALUES, { MONTHLY_EXPENSES } from '../definitions/MonthlyExpensesDefaultValues';
import { getMonthlyExpenses } from '../functions/getMonthlyExpensesCalculations';
import getMortgageScenarioChooserOptionsPayment from '../functions/getMortgageScenarioChooserOptionsPayment';
import getSelectedPaymentCalculatorScenario from '../functions/getSelectedPaymentCalculatorScenario';
import calculatePropertyTaxAmount from '../functions/calculatePropertyTaxAmount';
import PaymentCalculatorScenarioCalculations from './PaymentCalculatorScenarioCalculations';


function PaymentCalculatorStepMonthlyExpenses({
    stepNumber,
    scenarioType,
    scenarioCount,
    annualPropertyTax,
    onToggleExpand,
    onChangeScenario,
    ...otherProps
}) {
    const form = useForm();
    const calculatorState = useCalculator();
    const document = calculatorState.document;

    const {
        selectedScenarioNumber,
        homeType,
        askingPrice,
        location,
    } = form.values;

    // NOTE: getSavedMonthlyExpenses will return default values if no saved values are present
    const monthlyExpenses = getSavedMonthlyExpenses(
        toJS(document?.journey?.calculators),
        { askingPrice, location },
    );

    // NOTE: length of monthlyExpenses will always be constant
    // therefore its safe to add the values to dependency array
    const lines = useMemo(
        () => getLines(homeType, monthlyExpenses, annualPropertyTax),
        [ homeType, ...Object.values(monthlyExpenses) ],
    );

    return (
        <>
            <FormComputed
                name="monthlyMortgagePayment"
                value={getMonthlyMortgagePayment}
            />

            <FormComputed
                name="monthlyExpenses"
                value={values =>
                    getMonthlyExpenses({
                        mortgagePayment: values.monthlyMortgagePayment,
                        propertyInsuranceCalculation: values.propertyInsurance,
                        phoneCalculation: values.phone,
                        cableCalculation: values.cable,
                        internetCalculation: values.internet,
                        propertyTaxCalculation: values.propertyTax,
                        utilitiesCalculation: values.utilities,
                        monthlyDebtCalculation: values.debtPayments,
                        ...(homeType === HOME_TYPES.CONDO && {
                            condoFeesCalculation: values.condoFees,
                        }),
                    })
                }
            />

            <CalculatorStepAccordion
                accordionDataName={`calculator-step[${stepNumber}].mtg-paymentCalculator-monthlyExpensesAccordion`}
                title={(
                    <FormattedMessage
                        id="mtg-calculators.PaymentCalculatorStepMonthlyExpenses.title"
                        defaultMessage="Monthly expenses"
                    />
                )}
                showBadge={false}
                stepNumber={stepNumber}
                value="monthlyExpenses"
                valueFormat="CAD0"
                onToggleExpand={onToggleExpand}
                canBeDisabled
                {...otherProps}
            >
                <PaymentCalculatorScenarioCalculations
                    lines={lines}
                    result={{
                        name: 'monthlyExpensesResult',
                        label: (
                            <FormattedMessage
                                id="mtg-calculators.PaymentCalculatorStepMonthlyExpenses.monthlyExpensesResult"
                                defaultMessage="Monthly expenses"
                            />
                        ),
                        value: 'monthlyExpenses',
                    }}
                >
                    <If condition={scenarioCount > 1}>
                        <label
                            className="rh-title-xs rh-mb-1 rh-display-block weight-medium"
                            htmlFor="scenarioOptions"
                        >
                            <FormattedMessage
                                id="calculators.PaymentCalculatorStepMonthlyExpenses.downPaymentOptionsLabel"
                                defaultMessage="Down payment options"
                            />
                        </label>

                        <Select
                            id="scenarioOptions"
                            name="scenarioOptions"
                            options={getMortgageScenarioChooserOptionsPayment(scenarioType, scenarioCount)}
                            value={getSelectedPaymentCalculatorScenario(selectedScenarioNumber, scenarioCount)}
                            onChange={onChangeScenario}
                            type="dropdown"
                        />
                    </If>
                </PaymentCalculatorScenarioCalculations>

            </CalculatorStepAccordion>
        </>
    );
}

PaymentCalculatorStepMonthlyExpenses.propTypes = {
    scenarioType: PropTypes.oneOf(Object.values(SCENARIO_TYPES)),
    annualPropertyTax: PropTypes.number,
    stepNumber: PropTypes.number.isRequired,
    scenarioCount: PropTypes.number.isRequired,
    introduction: MessagePropType,

    onChangeScenario: PropTypes.func.isRequired,
    onToggleExpand: PropTypes.func,
};

PaymentCalculatorStepMonthlyExpenses.defaultProps = {
    scenarioType: SCENARIO_TYPES.PURCHASE,
    annualPropertyTax: undefined,
    introduction: undefined,
    onToggleExpand: undefined,
};

function getSavedMonthlyExpenses(savedCalculatorData, { askingPrice, location }) {
    const savedExpenses = savedCalculatorData?.contacts?.[0]?.expenses;

    const calculatedPropertyTax = calculatePropertyTaxAmount({
        location,
        homePrice: askingPrice,
    });

    const defaultValues = {
        ...MONTHLY_EXPENSES_DEFAULT_VALUES,
        [MONTHLY_EXPENSES.PROPERTY_TAX]: calculatedPropertyTax ?? 0,
    };

    if (!savedExpenses) {
        return defaultValues;
    }

    return Object.values(MONTHLY_EXPENSES).reduce((monthlyExpenses, expense) =>
        Object.assign(monthlyExpenses, {
            [expense]: savedExpenses?.find(saved => saved?.type === expense)?.value
                ?? defaultValues[expense],
        }), {});
}

function getMonthlyMortgagePayment(values) {
    const mortgagePayment = _get(
        values,
        `scenarios[${values.selectedScenarioNumber - 1}].mortgagePayment`,
    );
    const paymentFrequency = _get(
        values,
        `scenarios[${values.selectedScenarioNumber - 1}].paymentFrequency`,
    );

    const { frequency, divisor } = getPaymentFrequencyAndDivisor(paymentFrequency);

    // Calculate the yearly amount by multiplying value by frequency and divisor
    //  and then dividing 12 to get the monthly amount
    return mortgagePayment * frequency * divisor / 12;
}

function getLines(homeType, values, annualPropertyTax) {
    const houseLines = [
        {
            id: 'mortgagePayment',
            value: 'monthlyMortgagePayment',
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepMonthlyExpenses.downPaymentCalculationLabel"
                    defaultMessage="Mortgage payment"
                />
            ),
        },
        {
            id: MONTHLY_EXPENSES.PROPERTY_TAX,
            value: {
                name: 'propertyTax',
                type: 'integer',
                // NOTE: formula is converted to string to avoid a TypeError in FormattedNumberInput
                formula: ({ location, askingPrice }) => `${
                    calculatePropertyTaxAmount({
                        propertyTax: annualPropertyTax,
                        location,
                        homePrice: askingPrice,
                    }) ?? 0
                }`,
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepMonthlyExpenses.propertyTaxLabel"
                    defaultMessage="Property tax"
                />
            ),
        },
        {
            id: MONTHLY_EXPENSES.DEBT_PAYMENTS,
            value: {
                name: 'debtPayments',
                type: 'integer',
                value: values[MONTHLY_EXPENSES.DEBT_PAYMENTS],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepMonthlyExpenses.debtPaymentsLabel"
                    defaultMessage="Monthly debt payments"
                />
            ),
        },
        {
            id: MONTHLY_EXPENSES.UTILITIES,
            value: {
                name: 'utilities',
                type: 'integer',
                value: values[MONTHLY_EXPENSES.UTILITIES],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepMonthlyExpenses.pstCalculationLabel"
                    defaultMessage="Utilities"
                />
            ),
        },
        {
            id: MONTHLY_EXPENSES.PROPERTY_INSURANCE,
            name: 'propertyInsurance',
            value: {
                name: 'propertyInsurance',
                type: 'integer',
                value: values[MONTHLY_EXPENSES.PROPERTY_INSURANCE],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepMonthlyExpenses.lawyerFeeLabel"
                    defaultMessage="Property insurance"
                />
            ),
        },
        {
            id: MONTHLY_EXPENSES.PHONE,
            value: {
                name: 'phone',
                type: 'integer',
                value: values[MONTHLY_EXPENSES.PHONE],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepMonthlyExpenses.titleInsuranceLabel"
                    defaultMessage="Phone"
                />
            ),
        },
        {
            id: MONTHLY_EXPENSES.CABLE,
            value: {
                name: 'cable',
                type: 'integer',
                value: values[MONTHLY_EXPENSES.CABLE],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepMonthlyExpenses.homeInspectionLabel"
                    defaultMessage="Cable"
                />
            ),
        },
        {
            id: MONTHLY_EXPENSES.INTERNET,
            value: {
                name: 'internet',
                type: 'integer',
                value: values[MONTHLY_EXPENSES.INTERNET],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepMonthlyExpenses.appraisalFeeLabel"
                    defaultMessage="Internet"
                />
            ),
        },
    ];

    const condoLines = [
        {
            id: MONTHLY_EXPENSES.CONDO_FEES,
            value: {
                name: 'condoFees',
                type: 'integer',
                value: values[MONTHLY_EXPENSES.CONDO_FEES],
                formula: () => '0',
            },
            label: (
                <FormattedMessage
                    id="calculators.PaymentCalculatorStepMonthlyExpenses.condoFeesLabel"
                    defaultMessage="Condo fees"
                />
            ),
        },
    ];

    return homeType === HOME_TYPES.HOUSE
        ? [ ...houseLines ]
        : [ ...houseLines, ...condoLines ];
}


export default observer(PaymentCalculatorStepMonthlyExpenses);
